/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { DataProvider } from "./DataProvider";
import { IScheduleFiltersDatabase } from "sh-services/data/IScheduleFiltersDatabase";
import { IScheduleFiltersEntity } from "sh-models";
import { ScheduleFiltersStore, setScheduleFilters } from "sh-stores/sh-filters-store";

/**
 * The Schedule filters data provider.
 */
export class ScheduleFiltersDataProvider extends DataProvider<IScheduleFiltersEntity> {

    protected scheduleFiltersDatabase: IScheduleFiltersDatabase;
    protected teamId: string;

    constructor(scheduleFiltersDatabase: IScheduleFiltersDatabase, teamId: string) {
        super();
        this.scheduleFiltersDatabase = scheduleFiltersDatabase;
        this.teamId = teamId;
    }

    /**
     * Return data if it's found in memory (otherwise return undefined)
     */
    public async getDataFromMemory(): Promise<IScheduleFiltersEntity> {
        const { scheduleFilters } = ScheduleFiltersStore();

        if (this.teamId != scheduleFilters?.teamId) {
            return undefined;
        } else {
            return scheduleFilters;
        }
    }

    /**
     * Return data if it's found in the database (otherwise return undefined)
     */
    public async getDataFromDatabase(): Promise<IScheduleFiltersEntity> {
        return await this.scheduleFiltersDatabase.getScheduleFilters(this.teamId);
    }

    /**
     * Make an HTTP request to fetch the data from the network
     * NOTE: service calls for this do not exist yet
     */
    public async getDataFromNetwork(): Promise<IScheduleFiltersEntity> {
        throw new Error("ScheduleFiltersDataProvider.getDataFromNetwork not implemented");
    }

    /**
     * Set data in memory
     */
    public async setDataInMemory(data: IScheduleFiltersEntity): Promise<void> {
        setScheduleFilters(data);
    }

    /**
     * Set data in the database
     */
    public async setDataInDatabase(data: IScheduleFiltersEntity): Promise<void> {
        if (data) {
            return await this.scheduleFiltersDatabase.setScheduleFilters(data);
        }
    }

    /**
     * Make a network call to update the data
     * NOTE: service calls for this do not exist yet
     */
    public async saveDataToNetwork(): Promise<IScheduleFiltersEntity> {
        throw new Error("ScheduleFiltersDataProvider.saveDataToNetwork not implemented");
    }

}